import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { RavenApiSettings } from "./models/raven-api-settings";
import { BaseDataService } from "../../bbwt/modules/data-service/base.data.service";
import { HttpResponsesHandlersFactory, IHttpResponseHandlerSettings } from "@bbwt/modules/data-service";
import { IRavenLog } from "./models/raven-log";
import { IRavenLocation } from "./models/raven-location";
import { ITestApiResult } from "./models/test-api-result";
import { UserHelperService } from "@main/users/user-helper.service";
import { MiProModules } from "@mipro/classes/mipro-modules";
import { IEpisode } from "@project/models/episodes/episode";
import { IRavenIssue } from "./models/raven-issue";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { IOrganization, OrganizationService } from "@main/companies";
import { LinkUserService, CurrentUserChangedData, UserService } from "@main/users";
import { AppStorage } from "@bbwt/utils/app-storage";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { take } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Injectable({
    providedIn: "root"
})
export class RavenApiService extends BaseDataService {
    readonly url = "api/raven-api";
    public readonly entityTitle = "Raven API";

    constructor(protected http: HttpClient,
                handlersFactory: HttpResponsesHandlersFactory,
                private userHelperService: UserHelperService,
                private broadcastService: BroadcastService, private userService: UserService,
                private appOnlineStateService: AppOnlineStateService
                ) {
        super(http, handlersFactory);
        this.subscribeEvents();
    }

    initialize() {
        if (this.userHelperService.loadingClaims()) return;
        return this.loadRavenFields();
    }

    loadRavenFields(){
        this.updateRavenFields();
    }

    private subscribeEvents() {
        this.broadcastService
            .on(LinkUserService.UserSelectedOrganizationChangedEventName)
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.updateRavenFields());

        this.broadcastService
            .on<CurrentUserChangedData>(UserService.CurrentUserChangedEventName)
            .pipe(takeUntilDestroyed())
            .subscribe(data => {
                if (!data.isLogout) {
                    this.updateRavenFields();
                }
            });

        this.broadcastService
            .on<IOrganization>(OrganizationService.OrganizationChangedEventName)
            .pipe(takeUntilDestroyed())
            .subscribe(_ => this.updateRavenFields());
    }

    private updateRavenFields(){
        if (this.userService.isLogged) {
            this.checkLoginToken();
        }
    }

    test(item: RavenApiSettings): Promise<boolean> {
        return this.handleRequest<boolean>(
            this.http.post<boolean>(`${this.url}/testApi`, item)
        );
    }

    updateFieldsFromRaven(item: RavenApiSettings): Promise<boolean> {
        return this.handleRequest<boolean>(
            this.http.post<boolean>(`${this.url}/update-fields-from-raven`, item)
        );
    }

    checkLoginToken(): Promise<boolean> {
        return this.handleRequest<boolean>(
            this.http.get<boolean>(`${this.url}/check-login-token`), this.handlersFactory.getDefault()
        );
    }

    checkCategories(): Promise<boolean> {
        return this.handleRequest<boolean>(
            this.http.get<boolean>(`${this.url}/check-categories`), this.handlersFactory.getDefault()
        );
    }

    checkPriorities(): Promise<boolean> {
        return this.handleRequest<boolean>(
            this.http.get<boolean>(`${this.url}/check-priorities`), this.handlersFactory.getDefault()
        );
    }

    getSettings(): Promise<RavenApiSettings> {
        return this.handleRequest<RavenApiSettings>(
            this.http.get<RavenApiSettings>(`${this.url}/api-settings`)
        );
    }

    saveSettings(settings: RavenApiSettings): Promise<RavenApiSettings> {
        return this.handleRequest<RavenApiSettings>(
            this.http.post<RavenApiSettings>(`${this.url}/api-settings`, settings)
        );
    }

    _getAllEventsFromServer(): Promise<any> {
        var res = this.handleRequest<IRavenLog>(
            this.http.get<IRavenLog>(`${this.url}/getlogs`),
            this.handlersFactory.getDefault()
        );

        return res
    }

    async getAllEvents(): Promise<any> {
        if (this.appOnlineStateService.isAppOnline) {
            var options = await this._getAllEventsFromServer();
            AppStorage.setItem("eventsOptions", options)
            this.cacheLocationsForAllEvents(options);
        } else {
            var options = AppStorage.getItem<any>("eventsOptions");
        }
        return options;
    }

    private async cacheLocationsForAllEvents(events: IRavenLog[]): Promise<void> {
        try {
            const locations = ((await this._getAllLocationsFromServer()) as IRavenLocation[]) ?? [];
            AppStorage.setItem("locationsOptions", locations);
            for (const event of events ?? []) {
                AppStorage.setItem(`locationsOptions-${event.id}`, locations.filter(l => String(l.logId) === event.id));
            }
        } catch {
            // best effort offline cache warm-up
        }
    }

    _getAllLocationsFromServer(): Promise<any> {

        return this.handleRequest<IRavenLocation>(
            this.http.get<IRavenLocation>(`${this.url}/get-locations`),
            this.handlersFactory.getDefault()
        );

    }

    async getAllLocations(): Promise<any> {
        if (this.appOnlineStateService.isAppOnline) {
            var options = await this._getAllLocationsFromServer();
            AppStorage.setItem("locationsOptions", options)
        } else {
            var options = AppStorage.getItem<any>("locationsOptions");
        }
        return options;
    }


    _getLocationsByLogFromServer(logId: string): Promise<any> {

        return this.handleRequest<IRavenLocation>(
            this.http.get<IRavenLocation>(`${this.url}/get-locations/${logId}`),
            this.handlersFactory.getDefault()
        );

    }

    async getLocationsByLog(logId: string): Promise<IRavenLocation[]> {
        let options:IRavenLocation[] = [];

        if (this.appOnlineStateService.isAppOnline) {
            options = await this._getLocationsByLogFromServer(logId);
            AppStorage.setItem(`locationsOptions-${logId}`, options)
        } else {
            options = AppStorage.getItem<any>(`locationsOptions-${logId}`);
            if (!options) {
                const allLocations = AppStorage.getItem<IRavenLocation[]>("locationsOptions") ?? [];
                options = allLocations.filter(l => String(l.logId) === logId);
            }
        }
        return options;
    }

    checkStatuses(): Promise<boolean> {
        return this.handleRequest<boolean>(
            this.http.get<boolean>(`${this.url}/check-statuses`), this.handlersFactory.getDefault()
        );
    }

    isRavenModuleEnabled(): boolean{
        return this.userHelperService.hasModuleEnabled(MiProModules.Raven);
    }

    closeIssue(episode: IEpisode): Promise<void> {
        return this.handleRequest<void>(this.http.post<void>(`${this.url}/close-issue`,episode),
        this.handlersFactory.getDefault()
        );
    }

    async ravenConfigIsValid(): Promise<boolean> {
        try {
            return await this.handleRequest<boolean>(
                this.http.get<boolean>(`${this.url}/is-config-valid`), this.handlersFactory.getDefault()
            );
        } catch {
            // offline with no cached response: assume valid so raven fields stay usable
            return !this.appOnlineStateService.isAppOnline;
        }
    }

    updateLogs(): Promise<boolean> {
        return this.handleRequest<boolean>(
            this.http.post<boolean>(`${this.url}/update-logs`, null), this.handlersFactory.getDefault()
        );
    }

    updateLocations(): Promise<boolean> {
        return this.handleRequest<boolean>(
            this.http.post<boolean>(`${this.url}/update-locations`, null), this.handlersFactory.getDefault()
        );
    }

    isThereADefaultLog(): Promise<string> {
        return this.handleRequest<string>(
            this.http.get<string>(`${this.url}/default-log`), this.handlersFactory.getDefault()
        );
    }

    updateDefaultLog(defaultLog: IRavenLog): Promise<boolean> {
        return this.handleRequest<boolean>(
            this.http.post<boolean>(`${this.url}/update-default-log`, defaultLog), this.handlersFactory.getDefault()
        );
    }
}